import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Logo image URL - LOCKED, DO NOT MODIFY
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_0bb5453a-baec-4203-8db8-6150bde50842/artifacts/u0fyk3gu_IMG_1369.jpeg';

const OpeningScreen = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);
  const [isReceptive, setIsReceptive] = useState(false);

  // Progress through phases
  useEffect(() => {
    const timings = [
      2000,   // Phase 0 -> 1: Initial stillness, then logo appears
      3500,   // Phase 1 -> 2: Logo visible, then "Vaulten" appears
      3000,   // Phase 2 -> 3: Word visible, then "welcome" appears
      3000,   // Phase 3 -> 4: Welcome visible, then "by Alethera" appears
      4000,   // Phase 4 -> 5: All visible, held moment
    ];

    if (phase < 5) {
      const timer = setTimeout(() => {
        setPhase(prev => prev + 1);
      }, timings[phase]);
      return () => clearTimeout(timer);
    } else {
      // After final phase, become receptive to touch
      const timer = setTimeout(() => {
        setIsReceptive(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  // Handle transition to main experience
  const handleTransition = () => {
    if (isReceptive) {
      onComplete();
    }
  };

  // Also listen for keyboard
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isReceptive && (e.key === ' ' || e.key === 'Enter' || e.key === 'ArrowRight')) {
        e.preventDefault();
        onComplete();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isReceptive, onComplete]);

  return (
    <motion.div
      className="fixed inset-0 bg-background flex flex-col items-center z-50 cursor-pointer"
      onClick={handleTransition}
      onTouchEnd={handleTransition}
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 2.5, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {/* Logo in top 1/3 of screen */}
      <AnimatePresence>
        {phase >= 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 3, ease: [0.25, 0.1, 0.25, 1] }}
            className="absolute top-[8%] left-1/2 -translate-x-1/2"
            style={{
              maskImage: 'radial-gradient(ellipse 70% 70% at center, black 40%, transparent 100%)',
              WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at center, black 40%, transparent 100%)'
            }}
          >
            <img 
              src={LOGO_URL} 
              alt="Vaulten" 
              className="w-28 h-28 sm:w-32 sm:h-32 object-contain"
              style={{ 
                filter: 'brightness(1.1) contrast(1.05)'
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Vaulten wordmark - always present but invisible until phase 2 */}
      <motion.h1
        initial={false}
        animate={{ opacity: phase >= 2 ? 1 : 0 }}
        transition={{ duration: 2.5, ease: [0.25, 0.1, 0.25, 1] }}
        className="absolute top-1/2 left-1/2 font-display text-xl sm:text-2xl tracking-[0.4em] uppercase pointer-events-none"
        style={{ 
          color: 'hsl(40, 12%, 78%)',
          transform: 'translate(-50%, -50%)',
          willChange: 'opacity'
        }}
      >
        Vaulten
      </motion.h1>

      {/* Welcome - always present but invisible until phase 3 */}
      <motion.p
        initial={false}
        animate={{ opacity: phase >= 3 ? 0.6 : 0 }}
        transition={{ duration: 2.5, ease: [0.25, 0.1, 0.25, 1] }}
        className="absolute top-[58%] left-1/2 font-display text-lg sm:text-xl tracking-[0.2em] italic pointer-events-none"
        style={{ 
          color: 'hsl(40, 10%, 65%)',
          transform: 'translateX(-50%)',
          willChange: 'opacity'
        }}
      >
        welcome
      </motion.p>

      {/* By Alethera - always present at bottom */}
      <motion.p
        initial={false}
        animate={{ opacity: phase >= 4 ? 0.35 : 0 }}
        transition={{ duration: 2, ease: [0.25, 0.1, 0.25, 1] }}
        className="fixed bottom-10 left-1/2 font-body text-xs tracking-[0.15em] pointer-events-none"
        style={{ 
          color: 'hsl(40, 5%, 50%)',
          transform: 'translateX(-50%)',
          willChange: 'opacity'
        }}
      >
        by Alethera
      </motion.p>

      {/* Subtle touch indicator - only when receptive */}
      <AnimatePresence>
        {isReceptive && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            transition={{ duration: 2, ease: [0.25, 0.1, 0.25, 1] }}
            className="fixed bottom-24 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 5, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              className="w-px h-8"
              style={{ backgroundColor: 'hsl(40, 10%, 40%)' }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default OpeningScreen;
